import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { createAppQueryClient } from "@/lib/query-client";
import { LazyClerkProvider } from "@/auth/LazyClerkProvider";
import { PublicAuthModalProvider } from "@/app/PublicAuthModalContext";
import { ScrollToTop } from "@/components/ScrollToTop";

const queryClient = createAppQueryClient();

const Landing = lazy(() => import("@/pages/Landing"));
const Demo = lazy(() => import("@/pages/Demo"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Conversations = lazy(() => import("@/pages/Conversations"));
const Playground = lazy(() => import("@/pages/Playground"));
const MyAgent = lazy(() => import("@/pages/MyAgent"));
const Clients = lazy(() => import("@/pages/Clients"));
const Appointments = lazy(() => import("@/pages/Appointments"));
const Settings = lazy(() => import("@/pages/Settings"));
const Services = lazy(() => import("@/pages/Services"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Connectors = lazy(() => import("@/pages/Connectors"));
const CommentAutomations = lazy(() => import("@/pages/CommentAutomations"));
const WhatsAppTemplates = lazy(() => import("@/pages/WhatsAppTemplates"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AuthShell = lazy(() =>
  import("./AuthShell").then((module) => ({ default: module.AuthShell }))
);
const AppShell = lazy(() =>
  import("./AppShell").then((module) => ({ default: module.AppShell }))
);
const AppLayout = lazy(() =>
  import("@/components/layout/AppLayout").then((module) => ({ default: module.AppLayout }))
);
const ProtectedRoute = lazy(() =>
  import("@/components/auth/ProtectedRoute").then((module) => ({ default: module.ProtectedRoute }))
);

function RouteFallback() {
  return <div className="min-h-screen bg-background" />;
}

export function PublicShell() {
  return (
    <QueryClientProvider client={queryClient}>
      <LazyClerkProvider>
        <BrowserRouter>
          <PublicAuthModalProvider>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="/register" element={<Navigate to="/registro" replace />} />

                <Route element={<AuthShell />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/registro" element={<Register />} />
                </Route>

                <Route element={<AppShell />}>
                  <Route element={<ProtectedRoute />}>
                    <Route path="/onboarding" element={<Onboarding />} />

                    <Route element={<AppLayout />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/conversations" element={<Conversations />} />
                      <Route path="/agent" element={<MyAgent />} />
                      <Route path="/playground" element={<Navigate to="/agent?tab=simulator" replace />} />
                      <Route path="/clients" element={<Clients />} />
                      <Route path="/pipeline" element={<Pipeline />} />
                      <Route path="/appointments" element={<Appointments />} />
                      <Route path="/connectors" element={<Connectors />} />
                      <Route path="/comment-automations" element={<CommentAutomations />} />
                      <Route path="/whatsapp-templates" element={<WhatsAppTemplates />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/services" element={<Services />} />
                    </Route>
                  </Route>
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </PublicAuthModalProvider>
        </BrowserRouter>
      </LazyClerkProvider>
    </QueryClientProvider>
  );
}
